import {
  DefaultDialogsContainerNames,
  TextLibrary,
  CinnamonActionNames,
  CinnamonVariables,
  TemplateActionNames,
} from '../consts/defaultConfigValues/defaultConstants'
import type { RootState } from '../redux/store'

/**
 * Props for one of the dialogs the place-order chain opens, by name.
 *
 * ⚠️ **`CONFIRM_ORDER` is not a validator, and pushing it into the validator
 * list would show it first.** `useLoadDialogValidations` drains that list with
 * `pop()`, so the last entry opens first — the confirm dialog reaches this
 * function from that hook's `else` branch instead, once every validator has
 * been accepted. `validationsList` in `hooks/usePlaceOrder.ts` is the list of
 * names that may legitimately appear in `defaultSlice.validators`.
 *
 * ⚠️ **`state` is only consulted on CONFIRM_ORDER (COM-1279).** It picks the
 * two button-label tokens: `TextLibrary.CART_CONFIRM_ACTIONS_{CONFIRM,CANCEL}`
 * when the storefront authored `texts.cart.confirmActions.{confirm,cancel}`,
 * else today's `ACTIONS_YES` / `ACTIONS_CANCEL`. The JS check at the build
 * site is the fallback mechanism — the new tokens ship with no `||` operator
 * fallback because `texts` is a constantSource (see the `CART_PLACE_BUTTON`
 * docstring). Every other validator case ignores `state`, so existing callers
 * pass none and keep today's behavior.
 */

const getValidatorDialogsProps = (
  validator: string,
  state?: RootState,
) => {
  switch (validator) {
    case DefaultDialogsContainerNames.VALIDATE_ALCOHOL: {
      return {
        show: true,
        container: DefaultDialogsContainerNames.VALIDATE_ALCOHOL,
        title: { value: '' },
        id: 'alcoholdialog-0001',
        icon: {
          name: 'MdWarningAmber',
        },
        message: {
          value:
            '{{default.sessionData.configuration.checkoutRules.cartConditionPendingUserValidation.message}}',
        },
        buttons: [
          {
            label: TextLibrary.ACTIONS_ACCEPT,
            actions: {
              cinnamonActions: [
                {
                  name: CinnamonActionNames.CLOSE_DIALOG,
                },
                {
                  name: CinnamonActionNames.LOAD_DIALOGS_VALIDATIONS,
                  args: {
                    validators: '{{default.validators}}',
                  },
                },
              ],
            },
          },
          {
            label: TextLibrary.ACTIONS_CANCEL,
            color: 'inherit',
            variant: 'outlined',
            actions: {
              cinnamonActions: [
                {
                  name: CinnamonActionNames.CLOSE_DIALOG,
                  args: {
                    container: DefaultDialogsContainerNames.VALIDATE_ALCOHOL,
                    rejected: true,
                  },
                },
                {
                  name: TemplateActionNames.PLACE_ORDER_CLEAR,
                },
              ],
            },
          },
        ],
      }
    }
    case DefaultDialogsContainerNames.VALIDATE_RETURNABLE: {
      return {
        show: true,
        title: { value: '' },
        id: 'returnabledialog-0002',
        container: DefaultDialogsContainerNames.VALIDATE_RETURNABLE,
        icon: {
          name: 'MdWarningAmber',
        },
        message: {
          value:
            '{{default.sessionData.configuration.checkoutRules.cartWarningCheckReturnables.message}}',
        },
        buttons: [
          {
            label: TextLibrary.ACTIONS_ACCEPT,
            actions: {
              cinnamonActions: [
                {
                  name: 'closeDialog',
                },
                {
                  name: CinnamonActionNames.LOAD_DIALOGS_VALIDATIONS,
                  args: {
                    validators: '{{default.validators}}',
                  },
                },
              ],
            },
          },
          {
            label: TextLibrary.ACTIONS_CANCEL,
            color: 'inherit',
            variant: 'outlined',
            actions: {
              cinnamonActions: [
                {
                  name: 'closeDialog',
                  args: {
                    rejected: true,
                    container: DefaultDialogsContainerNames.VALIDATE_RETURNABLE,
                  },
                },
                {
                  name: TemplateActionNames.PLACE_ORDER_CLEAR,
                },
              ],
            },
          },
        ],
      }
    }
    case DefaultDialogsContainerNames.CONFIRM_ORDER: {
      /*
       * Per-button label scoping (COM-1279). Pick the dedicated token when the
       * storefront has authored `texts.cart.confirmActions.{confirm,cancel}`,
       * else keep today's `ACTIONS_YES`/`ACTIONS_CANCEL`. The check is on
       * presence of a truthy string, so authoring an empty string reverts to
       * the shared `actions.*` token rather than drawing a blank button — the
       * same stance `defaultProps/CartList.ts` takes on `deleteActions`.
       *
       * ⚠️ Picks the token *statically* here. `||`-fallback on the token
       * itself would bake the right-hand literal in at boot before
       * `mergedTexts` is assigned (see `CART_PLACE_BUTTON` docstring), so the
       * decision lives at the build site rather than inside the enum value.
       */
      const rawConfirmActions =
        state?.defaultSlice.config?.texts?.cart?.confirmActions
      /*
       * `ITextGroup` keys the second level as `string | { [key: string]: string }`,
       * so narrow before reading `.confirm`/`.cancel`. A storefront that
       * authored a flat string there — against the key's documented shape —
       * takes the fallback token rather than crashing.
       */
      const confirmActions =
        rawConfirmActions && typeof rawConfirmActions === 'object'
          ? rawConfirmActions
          : undefined
      const confirmLabel = confirmActions?.confirm
        ? TextLibrary.CART_CONFIRM_ACTIONS_CONFIRM
        : TextLibrary.ACTIONS_YES
      const cancelLabel = confirmActions?.cancel
        ? TextLibrary.CART_CONFIRM_ACTIONS_CANCEL
        : TextLibrary.ACTIONS_CANCEL
      return {
        show: true,
        id: 'confirmOrderdialog-0003',
        container: DefaultDialogsContainerNames.CONFIRM_ORDER,
        icon: {
          name: 'MdCheck',
        },
        title: {
          value: TextLibrary.CART_CONFIRM_TITLE,
        },
        message: {
          value: TextLibrary.CART_CONFIRM_MESSAGE,
        },
        buttons: [
          {
            label: confirmLabel,
            actions: {
              cinnamonActions: [
                {
                  name: CinnamonActionNames.CREATE_ORDER,
                  args: {
                    sessionId: CinnamonVariables.SESSION_ID,
                    storeFrontName: CinnamonVariables.STOREFRONT_NAME,
                    phone: CinnamonVariables.WORKFLOW_ID,
                    cart: CinnamonVariables.CART,
                    session: CinnamonVariables.SESSION_DATA,
                  },
                },
                {
                  name: CinnamonActionNames.CLOSE_DIALOG,
                },
                {
                  name: TemplateActionNames.PLACE_ORDER_FLOW_NEXT,
                },
              ],
            },
          },
          {
            label: cancelLabel,
            color: 'inherit',
            variant: 'outlined',
            actions: {
              cinnamonActions: [
                {
                  name: CinnamonActionNames.CLOSE_DIALOG,
                  args: {
                    rejected: true,
                    container: DefaultDialogsContainerNames.CONFIRM_ORDER,
                  },
                },
                {
                  name: TemplateActionNames.PLACE_ORDER_CLEAR,
                },
              ],
            },
          },
        ],
      }
    }
    default: {
      return {
        show: false,
        title: { value: '' },
        icon: {
          name: '',
        },
        message: {
          value: '',
        },
        buttons: [],
      }
    }
  }
}

export default getValidatorDialogsProps
